import React, { useEffect, useState } from "react";
import { ShoppingBag, Plus, Check, FileDown, Loader2, Zap, Lock } from "lucide-react";
import { PageHero, SectionTitle } from "@/components/Bits";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { useCart } from "@/components/CartProvider";
import { getProducts, startCheckout } from "@/lib/api";
import { IMG, MERCH_PREVIEW } from "@/lib/images";
import { toast } from "sonner";

const money = (c) => `$${(c / 100).toFixed(2)}`;

export default function Shop() {
  const { add, items } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    getProducts().then((d) => setProducts(d.shop)).catch(() => toast.error("Could not load the shop.")).finally(() => setLoading(false));
  }, []);

  const buyNow = async (p) => {
    setBuying(p.lookup_key);
    try {
      const { checkout_url } = await startCheckout(p.lookup_key);
      window.location.href = checkout_url;
    } catch { toast.error("Could not start checkout."); setBuying(null); }
  };

  const inCart = (k) => items.some((i) => i.lookup_key === k);

  return (
    <div data-testid="shop-page">
      <PageHero
        eyebrow="Digital Shop"
        title="Printable resources that travel with you"
        subtitle="Thoughtfully designed printable downloads and media — instant, secure delivery. Every purchase helps keep crisis resources free for others."
        image={IMG.cozyBookmark}
        testid="shop-hero"
      />

      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-20">
        <SectionTitle eyebrow="Printable Downloads & Media" title="Carry calm in your pocket" />
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-bsn-gold" /></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((p, i) => (
              <Reveal key={p.lookup_key} delay={i * 80}>
                <div className="h-full bg-white rounded-2xl overflow-hidden card-tactile flex flex-col" data-testid={`shop-product-${p.lookup_key}`}>
                  <div className="h-40 bg-gradient-to-br from-bsn-navy to-bsn-midnight flex items-center justify-center">
                    <FileDown className="w-12 h-12 text-bsn-gold/70" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-serif text-lg font-semibold text-bsn-midnight leading-snug">{p.name}</h3>
                    <p className="text-sm text-bsn-sapphire/75 mt-2 flex-1">{p.blurb}</p>
                    <ul className="mt-3 space-y-1">
                      {p.features?.map((f) => (
                        <li key={f} className="flex items-center gap-2 text-xs text-bsn-sapphire/70">
                          <Check className="w-3.5 h-3.5 text-bsn-gold" /> {f}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-5 flex items-center justify-between">
                      <span className="font-display text-2xl font-bold gold-text">{money(p.amount)}</span>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <Button onClick={() => add(p)} disabled={inCart(p.lookup_key)} variant="outline"
                        className="flex-1 border-bsn-gold/40 text-bsn-midnight hover:bg-bsn-gold/10 font-semibold"
                        data-testid={`shop-add-to-cart-${p.lookup_key}`}>
                        {inCart(p.lookup_key) ? <><Check className="w-4 h-4 mr-1" /> Added</> : <><Plus className="w-4 h-4 mr-1" /> Cart</>}
                      </Button>
                      <Button onClick={() => buyNow(p)} disabled={buying === p.lookup_key} className="flex-1 btn-gold font-semibold" data-testid={`shop-buy-now-${p.lookup_key}`}>
                        {buying === p.lookup_key ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4 mr-1" /> Buy</>}
                      </Button>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}

        <div className="mt-14 bg-bsn-canvas rounded-2xl p-6 flex items-center gap-4 card-tactile">
          <ShoppingBag className="w-8 h-8 text-bsn-bronze shrink-0" />
          <p className="text-sm text-bsn-sapphire/80">
            All items are digital downloads delivered instantly after secure Stripe checkout. Use test card
            <strong className="text-bsn-midnight"> 4242 4242 4242 4242</strong>, any future expiry and CVC.
          </p>
        </div>
      </section>

      {/* Merch preview */}
      <section className="bg-bsn-midnight text-bsn-parchment" data-testid="merch-preview-section">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto mb-4">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-bsn-gold/15 text-bsn-champagne border border-bsn-gold/40 text-xs font-mono uppercase tracking-wider">
              <Lock className="w-3.5 h-3.5" /> Preview · Launching Soon
            </span>
          </div>
          <SectionTitle eyebrow="BSN Merch" title="Wear & share the mission" subtitle="A first look at the Bright Stories Network collection. Checkout activates once your Printify &amp; Stripe accounts are connected." light />

          {["T-Shirts", "Mugs", "Journals"].map((cat) => (
            <div key={cat} className="mb-12 last:mb-0">
              <h3 className="font-serif text-2xl font-semibold text-bsn-champagne mb-5 flex items-center gap-3">
                <span className="w-8 h-px bg-bsn-gold" /> {cat}
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {MERCH_PREVIEW.filter((m) => m.category === cat).map((m, i) => (
                  <Reveal key={m.id} delay={i * 80}>
                    <div className="bg-bsn-navy/50 rounded-2xl overflow-hidden border border-bsn-gold/25 flex flex-col" data-testid={`merch-preview-${m.id}`}>
                      <div className="relative">
                        <img src={m.img} alt={m.name} className="w-full aspect-square object-cover" />
                        <span className="absolute top-3 right-3 text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full bg-bsn-gold text-bsn-midnight">Soon</span>
                      </div>
                      <div className="p-5 flex items-center justify-between gap-3">
                        <div>
                          <p className="font-serif text-lg font-semibold text-bsn-parchment leading-snug">{m.name}</p>
                          <p className="font-display text-lg font-bold gold-text">{m.price}</p>
                        </div>
                        <button disabled className="px-4 py-2 rounded-full bg-bsn-midnight/60 text-bsn-ice/50 text-sm font-semibold cursor-not-allowed shrink-0">
                          Coming Soon
                        </button>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}

          <p className="text-center text-sm text-bsn-ice/60 mt-4 max-w-2xl mx-auto">
            These are design previews. When you're ready, connect Printify (print-on-demand) and Stripe, and these become
            live products that print &amp; ship automatically — no inventory required.
          </p>
        </div>
      </section>
    </div>
  );
}
